import React, { useState, useEffect } from 'react';
import AdminNavbar from '../components/AdminNavbar';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const AdminUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { isAdmin } = useAuth();
    
    // Form validation errors
    const [validationErrors, setValidationErrors] = useState({});
    
    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    
    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role_name: 'user',
        name: ''
    });

    // Debounce search term
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        
        return () => {
            clearTimeout(timerId);
        };
    }, [searchTerm]);

    // Load users on component mount or when pagination/search changes
    useEffect(() => {
        fetchUsers();
    }, [pagination.page, pagination.limit, debouncedSearchTerm]);

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const options = {
                page: pagination.page,
                limit: pagination.limit,
                search: debouncedSearchTerm
            };
            
            const data = await userService.getAllUsers(options);
            
            setUsers(data.users);
            setPagination({
                ...pagination,
                total: data.pagination.total,
                totalPages: data.pagination.totalPages
            });
            setError(null);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
            toast.error('Failed to load users');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        // Reset to first page when searching
        setPagination({
            ...pagination,
            page: 1
        });
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination({
                ...pagination,
                page: newPage
            });
        }
    };

    // Handle items per page change
    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setPagination({
            ...pagination,
            page: 1, // Reset to first page when changing limit
            limit: newLimit
        });
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setFormData({
            username: '',
            email: '',
            password: '',
            role_name: 'user',
            name: ''
        });
        setValidationErrors({});
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            email: user.email,
            password: '', // Don't populate password for security
            role_name: user.role_name || 'user',
            name: user.name || ''
        });
        setValidationErrors({});
        setIsModalOpen(true);
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        // Additional check to prevent managers from deleting admins
        if (!isAdmin() && selectedUser.role_name === 'admin') {
            toast.error('Managers cannot delete admin users');
            setIsDeleteModalOpen(false);
            return;
        }

        try {
            setIsSubmitting(true);
            await userService.deleteUser(selectedUser.id);
            toast.success('User deleted successfully');
            setIsDeleteModalOpen(false);
            fetchUsers(); // Refresh the user list
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to delete user');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Validate form data
    const validateForm = () => {
        const errors = {};
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        
        if (!formData.username || formData.username.trim() === '') {
            errors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            errors.username = 'Username must be at least 3 characters';
        } else if (formData.username.length > 50) {
            errors.username = 'Username must be less than 50 characters';
        }
        
        if (!formData.email || formData.email.trim() === '') {
            errors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            errors.email = 'Invalid email format';
        }
        
        if (!editingUser && (!formData.password || formData.password.trim() === '')) {
            errors.password = 'Password is required for new users';
        } else if (formData.password && formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }
        
        if (!formData.role_name || formData.role_name.trim() === '') {
            errors.role_name = 'Role is required';
        }
        
        // Name validation
        if (formData.name && formData.name.length > 100) {
            errors.name = 'Name must be less than 100 characters';
        }
        
        // Display validation errors in UI
        setValidationErrors(errors);
        
        // Return true if no errors
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form before submission
        if (!validateForm()) {
            return;
        }
        
        // Additional check to prevent managers from editing admins
        if (
            !isAdmin() && 
            editingUser && 
            editingUser.role_name === 'admin'
        ) {
            toast.error('Managers cannot modify admin users');
            return;
        }

        // Additional check to prevent managers from creating or updating to admin role
        if (!isAdmin() && formData.role_name === 'admin') {
            toast.error('Only admins can create or update to admin role');
            return;
        }
        
        try {
            setIsSubmitting(true);
            
            if (editingUser) {
                // Update existing user
                const userData = { ...formData };
                // Remove password if not provided
                if (!userData.password) delete userData.password;
                
                await userService.updateUser(editingUser.id, userData);
                toast.success('User updated successfully');
            } else {
                // Create new user with properly formatted data
                const userData = {
                    username: formData.username.trim(),
                    email: formData.email.trim(),
                    password: formData.password,
                    role_name: formData.role_name,
                    name: formData.name ? formData.name.trim() : ''
                };
                
                await userService.createUser(userData);
                toast.success('User created successfully');
            }
            
            setIsModalOpen(false);
            fetchUsers(); // Refresh user list
        } catch (err) {
            console.error('Operation failed:', err);
            
            // Handle different types of errors
            if (err.response?.status === 400) {
                // Validation errors
                if (err.response.data?.errors) {
                    setValidationErrors(err.response.data.errors);
                    toast.error('Please fix the validation errors');
                } else {
                    toast.error(err.response.data?.error || 'Validation failed');
                }
            } else if (err.response?.status === 409) {
                // Conflict error (duplicate email/username)
                const field = err.response.data?.field || '';
                if (field === 'username') {
                    setValidationErrors({
                        ...validationErrors,
                        username: 'This username is already taken'
                    });
                } else if (field === 'email') {
                    setValidationErrors({
                        ...validationErrors,
                        email: 'This email is already registered'
                    });
                } else {
                    toast.error(err.response.data?.error || 'A user with this information already exists');
                }
            } else if (err.response?.status === 403) {
                // Permission error
                toast.error(err.response.data?.error || 'You do not have permission to perform this action');
            } else {
                // Server or network error
                toast.error(err.response.data?.error || 'Operation failed. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        // Clear validation error for this field when user types
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: undefined
            });
        }
    };

    // Render pagination controls
    const renderPagination = () => {
        const { page, totalPages } = pagination;
        
        if (totalPages <= 1) return null;
        
        // Create page numbers array with ellipsis for large page counts
        const getPageNumbers = () => {
            const pages = [];
            const maxVisiblePages = 5;
            
            if (totalPages <= maxVisiblePages) {
                // Show all pages if there are few
                for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Always include first page
                pages.push(1);
                
                // Calculate start and end of visible pages around current page
                let start = Math.max(2, page - 1);
                let end = Math.min(totalPages - 1, page + 1);
                
                // Add ellipsis if needed before start
                if (start > 2) {
                    pages.push('...');
                }
                
                // Add visible pages
                for (let i = start; i <= end; i++) {
                    pages.push(i);
                }
                
                // Add ellipsis if needed after end
                if (end < totalPages - 1) {
                    pages.push('...');
                }
                
                // Always include last page
                pages.push(totalPages);
            }
            
            return pages;
        };
        
        return (
            <div className="flex items-center justify-between px-4 py-3 sm:px-6 mt-4">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className={`relative inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium ${
                            page === 1
                                ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className={`relative inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium ${
                            page === totalPages
                                ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        Next
                    </button>
                </div>
                
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{users.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                            <span className="font-medium">
                                {Math.min(pagination.page * pagination.limit, pagination.total)}
                            </span>{' '}
                            of <span className="font-medium">{pagination.total}</span> results
                        </p>
                    </div>
                    <div>
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-700">Rows per page:</span>
                            <select
                                className="rounded border-gray-300 text-sm"
                                value={pagination.limit}
                                onChange={handleLimitChange}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                    </div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                            className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                                page === 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                        >
                            <span className="sr-only">Previous</span>
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                        
                        {getPageNumbers().map((pageNum, i) => (
                            pageNum === '...' ? (
                                <span 
                                    key={`ellipsis-${i}`} 
                                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white"
                                >
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                        pageNum === page
                                            ? 'bg-blue-600 text-white focus:z-20'
                                            : 'bg-white text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    {pageNum}
                                </button>
                            )
                        ))}
                        
                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                            className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                                page === totalPages
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                        >
                            <span className="sr-only">Next</span>
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-100">
                <AdminNavbar />
                <div className="flex justify-center items-center h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <AdminNavbar />
            <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <div className="px-4 sm:px-0 mb-6 flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900 mb-0">User Management</h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Manage user accounts and permissions.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
                        <div className="relative w-full md:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>
                        <button
                            onClick={handleAddUser}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" /> Add User
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-lg overflow-hidden shadow-md">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Username
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                            {debouncedSearchTerm ? 'No users found matching your search.' : 'No users found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    users.map(user => (
                                        <tr key={user.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {user.username}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.name || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    user.role_name === 'admin'
                                                        ? 'bg-purple-100 text-purple-800'
                                                        : user.role_name === 'manager'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : user.role_name === 'legal_professional'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {user.role_name}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                {/* Hide edit button for admin users if current user is a manager */}
                                                {!(user.role_name === 'admin' && !isAdmin()) && (
                                                    <button
                                                        onClick={() => handleEditUser(user)}
                                                        className="text-blue-600 hover:text-blue-900 mr-4"
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                                {/* Hide delete button for admin users if current user is a manager */}
                                                {!(user.role_name === 'admin' && !isAdmin()) && (
                                                    <button
                                                        onClick={() => handleDeleteClick(user)}
                                                        className="text-red-600 hover:text-red-900"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {renderPagination()}
                </div>

                {/* User Modal */}
                {isModalOpen && (
                    <div className="fixed z-50 inset-0 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full max-h-[70vh] overflow-y-auto relative" style={{ marginTop: '15vh' }}>
                                <form onSubmit={handleSubmit} className="p-6">
                                    <div className="flex items-center justify-between mb-4 border-b pb-3">
                                        <h3 className="text-xl font-semibold text-gray-900">
                                            {editingUser ? 'Edit User' : 'Add User'}
                                        </h3>
                                        <button 
                                            type="button" 
                                            onClick={() => setIsModalOpen(false)}
                                            className="text-gray-400 hover:text-gray-500 transition-colors"
                                        >
                                            <span className="sr-only">Close</span>
                                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    
                                    {Object.keys(validationErrors).length > 0 && (
                                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                            <div className="flex">
                                                <div className="flex-shrink-0">
                                                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <h3 className="text-sm font-medium text-red-800">
                                                        There {Object.keys(validationErrors).length === 1 ? 'is an error' : 'are errors'} with your submission
                                                    </h3>
                                                    <div className="mt-2 text-sm text-red-700">
                                                        <ul className="list-disc pl-5 space-y-1">
                                                            {Object.values(validationErrors).map((error, index) => (
                                                                <li key={index}>{error}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                                                Username*
                                            </label>
                                            <input
                                                type="text"
                                                id="username"
                                                name="username"
                                                value={formData.username}
                                                onChange={handleChange}
                                                className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                                                    validationErrors.username ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                }`}
                                            />
                                            {validationErrors.username && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <svg className="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    {validationErrors.username}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                                Email*
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                                                    validationErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                }`}
                                            />
                                            {validationErrors.email && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <svg className="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    {validationErrors.email}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                                Password {editingUser && <span className="text-xs text-gray-500">(leave blank to keep current)</span>}
                                            </label>
                                            <input
                                                type="password"
                                                id="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                                                    validationErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                }`}
                                            />
                                            {validationErrors.password && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <svg className="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    {validationErrors.password}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="role_name" className="block text-sm font-medium text-gray-700 mb-1">
                                                Role*
                                            </label>
                                            <select
                                                id="role_name"
                                                name="role_name"
                                                value={formData.role_name}
                                                onChange={handleChange}
                                                className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                                                    validationErrors.role_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                }`}
                                            >
                                                <option value="user">User</option>
                                                <option value="legal_professional">Legal Professional</option>
                                                <option value="manager">Manager</option>
                                                {isAdmin() && <option value="admin">Admin</option>}
                                            </select>
                                            {validationErrors.role_name && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <svg className="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    {validationErrors.role_name}
                                                </p>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    className="mt-1 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-6 border-t pt-4 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isSubmitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {isDeleteModalOpen && (
                    <div className="fixed z-50 inset-0 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative" style={{ marginTop: '20vh' }}>
                                <div className="bg-white px-6 pt-5 pb-4 sm:p-6 rounded-t-lg">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center">
                                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                                <TrashIcon className="h-6 w-6 text-red-600" />
                                            </div>
                                            <div className="mt-0 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                                <h3 className="text-xl leading-6 font-medium text-gray-900">
                                                    Delete User
                                                </h3>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        Are you sure you want to delete {selectedUser?.username}? This action cannot be undone.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <button 
                                            type="button" 
                                            onClick={() => setIsDeleteModalOpen(false)}
                                            className="text-gray-400 hover:text-gray-500"
                                        >
                                            <span className="sr-only">Close</span>
                                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-6 py-4 sm:px-6 sm:py-4 rounded-b-lg sm:flex sm:flex-row-reverse">
                                    <button
                                        type="button"
                                        onClick={handleDeleteConfirm}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-5 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Deleting...' : 'Delete'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsDeleteModalOpen(false)}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-5 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUsersPage; 